import React, { Component, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { parse } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { omit } from '../../util/common';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { Page } from '../../components';
import RacingThemeSearchBanner from '../../components/RacingThemeSearchBanner/RacingThemeSearchBanner';
import RacingThemeSearchBody from '../../components/RacingThemeSearchBody/RacingThemeSearchBody';
import {
  buildActiveFilterChips,
  buildBannerTabs,
  buildRacingFilterGroups,
  buildSortOptions,
  CATEGORY_QUERY_PARAM_NAMES,
  formatOptionChangeParams,
  formatPriceChangeParams,
  splitFiltersForRacingUi,
} from '../../components/RacingThemeSearch/searchPageFilterAdapters';
import { mapListingsToSearchCards } from '../../components/RacingThemeSearch/mapListingToSearchCard';
import { resolveDepartmentFromCategoryId } from '../../util/racingThemeDepartment';
import {
  FAVORITE_TYPE_LISTING,
  isFavorite as isListingFavorite,
} from '../../util/favorites';
import { toggleFavorite } from '../../ducks/favorites.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  initialValues,
  validUrlQueryParamsFromProps,
  getDerivedRenderData,
  onResetAll,
  createFilterValueChangeHandler,
  onSortBy,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchErrors from './SearchErrors';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768;
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleBannerSubmit = this.handleBannerSubmit.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handlePriceCommit = this.handlePriceCommit.bind(this);
    this.handleRemoveFilter = this.handleRemoveFilter.bind(this);
    this.handleRacingSortChange = this.handleRacingSortChange.bind(this);
    this.handleToggleFavorite = this.handleToggleFavorite.bind(this);
  }

  handleToggleFavorite(type, id) {
    const { currentUser, history, routeConfiguration, location, onToggleFavorite } = this.props;
    if (typeof onToggleFavorite === 'function') {
      onToggleFavorite(type, id);
      return;
    }
    if (!currentUser) {
      const state = {
        from: `${location.pathname}${location.search}${location.hash}`,
      };
      history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), state);
      return;
    }
    // Fallback if container forgot to pass handler (should not happen in production wiring)
  }

  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    onResetAll({
      history,
      routeConfiguration,
      config,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      setState: this.setState.bind(this),
    });
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;

    return createFilterValueChangeHandler(
      {
        history,
        routeConfiguration,
        config,
        location,
        currentPathParams,
        urlQueryParams: validUrlQueryParamsFromProps(this.props),
        setState: this.setState.bind(this),
        getState: () => this.state,
      },
      useHistoryPush
    );
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    onSortBy({
      history,
      routeConfiguration,
      location,
      urlQueryParams: validUrlQueryParamsFromProps(this.props),
      urlParam,
      values,
    });
  }

  handleResetAll(e) {
    this.resetAll(e);
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  handleBannerSubmit(query) {
    const { history, routeConfiguration } = this.props;
    const keywords = typeof query === 'string' ? query.trim() : '';
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const queryParams = keywords
      ? { ...urlQueryParams, keywords }
      : omit(urlQueryParams, 'keywords');

    const pageName = this.props.params?.listingType
      ? 'SearchPageWithListingType'
      : 'SearchPage';
    const pathParams = this.props.params?.listingType
      ? { listingType: this.props.params.listingType }
      : {};

    history.push(
      createResourceLocatorString(pageName, routeConfiguration, pathParams, {
        ...omit(queryParams, 'page'),
      })
    );
  }

  handleTabChange(tab) {
    const { history, routeConfiguration } = this.props;
    const categoryId = typeof tab === 'string' ? tab : tab?.id;
    const urlQueryParams = omit(validUrlQueryParamsFromProps(this.props), [
      'page',
      ...CATEGORY_QUERY_PARAM_NAMES,
    ]);

    const nextParams = categoryId
      ? { ...urlQueryParams, pub_categoryLevel1: categoryId }
      : urlQueryParams;

    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, {}, nextParams)
    );
  }

  handleOptionChange({ groupId, optionId, checked }, filterGroups) {
    const group = filterGroups.find(g => g.id === groupId);
    if (!group) {
      return;
    }
    const onSubmit = this.getHandleChangedValueFn(true);
    onSubmit(formatOptionChangeParams(group, optionId, checked));
  }

  handlePriceCommit({ groupId, min, max }, filterGroups) {
    const group = filterGroups.find(g => g.id === groupId);
    if (!group) {
      return;
    }
    const onSubmit = this.getHandleChangedValueFn(true);
    onSubmit(formatPriceChangeParams(group, { min, max }));
  }

  handleRemoveFilter(filter) {
    if (!filter || typeof filter === 'string') {
      return;
    }
    const onSubmit = this.getHandleChangedValueFn(true);
    onSubmit(filter.clearParams || {});
  }

  handleRacingSortChange(value) {
    const { config } = this.props;
    const sortConfig = config?.search?.sortConfig;
    if (!sortConfig?.active) {
      return;
    }
    const relevanceKey = sortConfig.relevanceKey;
    const nextValue = value === relevanceKey ? null : value;
    this.handleSortBy(sortConfig.queryParamName, nextValue);
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    const {
      listingTypePathParam,
      sortConfig,
      validQueryParams,
      availableFilters,
      selectedFilters,
      isValidDatesFilter,
      selectedFiltersCountForMobile,
      totalItems,
      listingsAreLoaded,
      conflictingFilterActive,
      showCreateListingsLink,
      title,
      description,
      schema,
      marketplaceCurrency,
      listingCategories,
    } = getDerivedRenderData({
      intl,
      location,
      config,
      routeConfiguration,
      searchParams,
      pagination,
      listings,
      searchInProgress,
      currentPathParams,
      currentUser,
    });

    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };

    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    const { racingFilters, leftoverFilters } = splitFiltersForRacingUi(availableFilters);
    const filterGroups = buildRacingFilterGroups({
      racingFilters,
      listingCategories,
      validQueryParams,
      intl,
    });
    const activeFilters = buildActiveFilterChips({ filterGroups, validQueryParams });
    const bannerTabs = buildBannerTabs(listingCategories || []);
    const sortOptions = buildSortOptions(sortConfig, intl, {
      includeRelevance: !!validQueryParams.keywords,
    });
    const activeSort =
      validQueryParams[sortConfig.queryParamName] ||
      (validQueryParams.keywords ? sortConfig.relevanceKey : sortOptions[0]?.value) ||
      '';

    const searchCards = listingsAreLoaded
      ? mapListingsToSearchCards(listings, { config, intl, routeConfiguration }).map(card => ({
          ...card,
          isFavorite: isListingFavorite(currentUser, FAVORITE_TYPE_LISTING, card.id),
        }))
      : [];

    const pageName = listingTypePathParam ? 'SearchPageWithListingType' : 'SearchPage';
    const pagePathParams = listingTypePathParam ? { listingType: listingTypePathParam } : {};
    const pageSearchParams = parse(location.search);
    const bannerQuery =
      pageSearchParams.keywords ||
      validQueryParams.keywords ||
      '';
    const activeTab = validQueryParams.pub_categoryLevel1 || '';
    const activeDepartment = resolveDepartmentFromCategoryId(
      activeTab,
      listingCategories || []
    );

    const leftoverFilterNodes =
      leftoverFilters.length > 0
        ? leftoverFilters.map(filterConfig => {
            const key = `RacingFilters.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
            const filterId = `RacingFilters.${filterConfig.key.toLowerCase()}`;
            return (
              <FilterComponent
                key={key}
                id={filterId}
                className={css.filter}
                config={filterConfig}
                containerId="SearchPageWithGrid_RacingLeftoverFilters"
                listingCategories={listingCategories}
                marketplaceCurrency={marketplaceCurrency}
                urlQueryParams={validQueryParams}
                initialValues={initialValues(this.props, this.state.currentQueryParams)}
                getHandleChangedValueFn={this.getHandleChangedValueFn}
                intl={intl}
                liveEdit
                showAsPopup={false}
                isDesktop
              />
            );
          })
        : null;

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          rootClassName={topbarClasses}
          currentSearchParams={validQueryParams}
        />

        <div
          className={css.racingSearchLayout}
          data-department={activeDepartment || undefined}
        >
          <RacingThemeSearchBanner
            query={bannerQuery}
            placeholder="Search listings, parts, experiences…"
            tabs={bannerTabs.length > 0 ? bannerTabs : ['Shop', 'Book', 'Partner']}
            activeTab={activeTab}
            onSubmit={this.handleBannerSubmit}
            onTabChange={this.handleTabChange}
          />

          <SearchFiltersMobile
            className={css.searchFiltersMobileList}
            urlQueryParams={validQueryParams}
            sortByComponent={sortBy('mobile')}
            listingsAreLoaded={listingsAreLoaded}
            resultsCount={totalItems}
            searchInProgress={searchInProgress}
            searchListingsError={searchListingsError}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
            onOpenModal={this.onOpenMobileModal}
            onCloseModal={this.onCloseMobileModal}
            resetAll={this.resetAll}
            selectedFiltersCount={selectedFiltersCountForMobile}
            isMapVariant={false}
            location={location}
          >
            {availableFilters.map(filterConfig => {
              const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                filterConfig.key
              }`;
              const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;

              return (
                <FilterComponent
                  key={key}
                  id={filterId}
                  config={filterConfig}
                  containerId="SearchPage_MobileFilters"
                  listingCategories={listingCategories}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={validQueryParams}
                  initialValues={initialValues(this.props, this.state.currentQueryParams)}
                  getHandleChangedValueFn={this.getHandleChangedValueFn}
                  intl={intl}
                  liveEdit
                  showAsPopup={false}
                />
              );
            })}
          </SearchFiltersMobile>

          <RacingThemeSearchBody
            filterGroups={filterGroups}
            activeFilters={activeFilters}
            listings={searchCards}
            count={listingsAreLoaded ? totalItems : 0}
            query={bannerQuery}
            sortOptions={sortOptions}
            activeSort={activeSort}
            onClearAll={e => this.handleResetAll(e)}
            onOptionChange={payload => this.handleOptionChange(payload, filterGroups)}
            onPriceCommit={payload => this.handlePriceCommit(payload, filterGroups)}
            onSortChange={this.handleRacingSortChange}
            onRemoveFilter={this.handleRemoveFilter}
            onToggleFavorite={this.handleToggleFavorite}
            pagination={listingsAreLoaded ? pagination : null}
            pageName={pageName}
            pagePathParams={pagePathParams}
            pageSearchParams={pageSearchParams}
            filterPanelChildren={leftoverFilterNodes}
            resultsChildren={
              <>
                <SearchErrors
                  searchListingsError={searchListingsError}
                  isValidDatesFilter={isValidDatesFilter}
                />
                {noResultsInfo}
              </>
            }
          />
        </div>

        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage "container" (grid layout): selects Redux state and dispatch handlers, then passes the
 * same prop surface as before to `SearchPageComponent` via `SearchPageAccessWrapper`.
 *
 * @param {Object} props - Router / route props from `routeConfiguration.js` and `Routes.js`
 * @returns {JSX.Element}
 */
const SearchPage = props => {
  const dispatch = useDispatch();
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);

  const currentUser = useSelector(state => state.user?.currentUser);
  const { pagination, searchInProgress, searchListingsError, searchParams } = useSelector(
    state => state.SearchPage
  );
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  const onToggleFavorite = useCallback(
    (type, id) => {
      if (!currentUser) {
        const { history, location, routeConfiguration } = props;
        const state = {
          from: `${location.pathname}${location.search}${location.hash}`,
        };
        history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}), state);
        return;
      }
      dispatch(toggleFavorite({ type, id })).catch(() => {});
    },
    [currentUser, dispatch, props]
  );

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageComponent}
      currentUser={currentUser}
      listings={listings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      onManageDisableScrolling={onManageDisableScrolling}
      onToggleFavorite={onToggleFavorite}
    />
  );
};

export default SearchPage;
